import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Header } from '../../components/Header';
import { Footer } from '../../components/Footer';
import { StoreBadges } from '../../components/StoreBadges';
import { LocaleProvider, useLocale } from '../../i18n/LocaleContext';
import { useSeo } from '../../seo/useSeo';
import { breadcrumbJsonLd } from '../../seo/breadcrumbs';
import { APP_STORE_URL, googlePlayUrl, isEventId } from '../../constants/storeLinks';
import { reportInstallIntent } from '../../utils/installIntent';
import logoWordmark from '../../assets/images/logo.png';
import '../../styles/landing.css';

type Platform = 'ios' | 'android' | 'other';

const detectPlatform = (): Platform => {
  if (typeof navigator === 'undefined') return 'other';
  const ua = navigator.userAgent || '';
  const isIOS = /iPad|iPhone|iPod/.test(ua) ||
    (navigator.platform === 'MacIntel' && (navigator as Navigator & { maxTouchPoints?: number }).maxTouchPoints! > 1);
  if (isIOS) return 'ios';
  if (/Android/i.test(ua)) return 'android';
  return 'other';
};

const GetAppInner = (): React.JSX.Element => {
  const { t } = useLocale();
  // Detect synchronously so the very first paint is already the right screen —
  // no flash of the marketing site before the redirect fires.
  const [platform] = useState<Platform>(() => detectPlatform());

  // Deferred deep link: the server-rendered event page sends shares here as
  // `/get?event=<uuid>`. On Android the id rides along to Play as an install
  // referrer; on iOS it is reported to the backend as an install intent. The
  // app opens that event on its first launch either way. Ignored when absent
  // or malformed — `/get` stays a plain install page.
  const [searchParams] = useSearchParams();
  const eventParam = searchParams.get('event');
  const eventId = isEventId(eventParam) ? eventParam : null;
  const playUrl = googlePlayUrl(eventId);

  useSeo({
    title: 'Get the App',
    description:
      'Download Gather for iOS and Android — discover events, join clubs, and host meetups in 60 seconds.',
    canonical: '/get',
    jsonLd: [
      breadcrumbJsonLd([
        { name: 'Home', path: '/' },
        { name: 'Get the app', path: '/get' },
      ]),
      {
        '@context': 'https://schema.org',
        '@type': 'MobileApplication',
        name: 'Gather — Enjoy real events',
        operatingSystem: 'iOS 16+, Android',
        applicationCategory: 'SocialNetworkingApplication',
        downloadUrl: APP_STORE_URL,
        installUrl: APP_STORE_URL,
        offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD' },
      },
    ],
  });

  useEffect(() => {
    if (platform === 'ios') {
      if (eventId) {
        // No install referrer on iOS, so record an install intent (event id +
        // coarse device signals) before handing off to the App Store; the app
        // claims it on first launch. The JSON POST needs a CORS preflight, and
        // Safari is unreliable about finishing preflighted requests once
        // navigation starts — so wait for it, but never longer than 400ms.
        // reportInstallIntent never rejects, so the redirect always fires.
        void Promise.race([
          reportInstallIntent(eventId),
          new Promise(resolve => setTimeout(resolve, 400)),
        ]).then(() => window.location.replace(APP_STORE_URL));
      } else {
        window.location.replace(APP_STORE_URL);
      }
    } else if (platform === 'android') {
      window.location.replace(playUrl);
    }
  }, [platform, playUrl, eventId]);

  // Mobile: clean branded splash while the store opens — not the full site.
  if (platform === 'ios' || platform === 'android') {
    const url = platform === 'ios' ? APP_STORE_URL : playUrl;
    const storeName = platform === 'ios' ? 'the App Store' : 'Google Play';
    return (
      <div className="get-splash">
        <style>
          {`@keyframes get-spin{to{transform:rotate(360deg)}}
            .get-splash{min-height:100vh;min-height:100svh;display:flex;flex-direction:column;
              align-items:center;justify-content:center;gap:22px;padding:32px;text-align:center;
              background:radial-gradient(60vw 60vw at 20% 0%,rgba(155,93,229,.28),transparent 60%),
              radial-gradient(50vw 50vw at 100% 100%,rgba(87,121,240,.22),transparent 60%),#08060f;color:#fff;}
            .get-splash img{height:52px;filter:drop-shadow(0 2px 12px rgba(155,93,229,.5));}
            .get-splash .ring{width:46px;height:46px;border-radius:50%;
              border:3px solid rgba(255,255,255,.15);border-top-color:#b98bff;animation:get-spin .9s linear infinite;}
            .get-splash h1{font-family:'Grandstander',system-ui,sans-serif;font-weight:800;font-size:22px;margin:0;}
            .get-splash p{color:#a99fc4;font-size:14px;margin:0;}
            .get-splash a{margin-top:6px;color:#c7b3ff;font-weight:600;font-size:14px;text-decoration:underline;}`}
        </style>
        <img src={logoWordmark} alt="Gather" />
        <div className="ring" />
        <h1>Opening {storeName}…</h1>
        <p>Hang tight — Gather is loading.</p>
        <a href={url}>Tap here if nothing happens</a>
      </div>
    );
  }

  return (
    <div className="landing" id="top">
      <Header />
      <main>
        <section style={{ padding: '80px 0', minHeight: '60vh' }}>
          <div className="wrap" style={{ display: 'grid', placeItems: 'center', textAlign: 'center', gap: 24 }}>
            <span className="eyebrow">{t('cta.eyebrow')}</span>
            <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 'clamp(28px, 4vw, 44px)', margin: 0 }}>
              {t('get.desktop.h')}
            </h2>
            <p style={{ color: '#4a4255', fontSize: 16, maxWidth: 520 }}>{t('get.desktop.p')}</p>
            <StoreBadges eventId={eventId} height={56} style={{ alignItems: 'center', marginTop: 8 }} />
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export const GetApp = (): React.JSX.Element => (
  <LocaleProvider>
    <GetAppInner />
  </LocaleProvider>
);
